import makeStyles from '@mui/styles/makeStyles';
import {Check} from "lucide-react";
import React from "react";
import {useTranslation} from "react-i18next";
import {color, font, radius, type} from "common/tokens";

const useStyles = makeStyles({
  rules: {
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    margin: '-6px 0 18px',
    padding: 0,
  },
  rule: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  met: {
    color: color.SUCCESS,
  },
  mark: {
    width: 14,
    height: 14,
    borderRadius: radius.PILL,
    background: color.DIVIDER,
    color: color.SURFACE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  markMet: {
    background: color.SUCCESS,
  },
  // `aria-label` on a listitem is not reliably exposed, so the state is read
  // from text that only a screen reader reaches.
  offscreen: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
});

// The policy used to be a static list you only found out you had failed after
// submitting. Each rule ticks as it is met.
export const passwordRules = [
  {key: 'passwordPolicyRequirementsLength', test: value => value.length >= 8},
  {key: 'passwordPolicyRequirementsLowerCase', test: value => /[a-z]/.test(value)},
  {key: 'passwordPolicyRequirementsUpperCase', test: value => /[A-Z]/.test(value)},
  {key: 'passwordPolicyRequirementsNumeral', test: value => /[0-9]/.test(value)},
  {key: 'passwordPolicyRequirementsSpecial', test: value => /[^a-zA-Z0-9]/.test(value)},
];

export const meetsPasswordPolicy = (password = "") =>
  passwordRules.every(rule => rule.test(password));

const PasswordRules = ({password = ""}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  return (
    <ul className={classes.rules} aria-live={'polite'}>
      {passwordRules.map(rule => {
        const met = rule.test(password);

        return (
          <li key={rule.key} className={`${classes.rule} ${met ? classes.met : ''}`}>
            <span className={`${classes.mark} ${met ? classes.markMet : ''}`} aria-hidden={true}>
              {met && <Check size={9} strokeWidth={3}/>}
            </span>
            {t(rule.key)}
            {met && <span className={classes.offscreen}>{`, ${t('completed')}`}</span>}
          </li>
        );
      })}
    </ul>
  );
};

export default PasswordRules;
